import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { BASE_URL as u } from "../../commonUrls";
import "./CommandPalette.css";

/**
 * Global "quick jump" command palette — open with Ctrl/Cmd+K (or the
 * "⌘K Quick Jump" sidebar button, which dispatches the same custom event).
 *
 * Two behaviors, both scoped by a leading "/module" token:
 *  - "/planner"       → navigate straight to the Planner page.
 *  - "/planner/rent"  → live-search Planner items matching "rent", inline,
 *                        click a result to open it directly.
 * A query with no leading "/" runs an unscoped live search across every
 * module (reuses the same /api/search endpoint SearchPage.jsx uses).
 *
 * Module → source-type mapping matches the `source` strings SearchResultMapper
 * emits on the backend (see SearchResultMapper.java) — only modules that are
 * actually indexed by a SearchContributor get in-palette search; the rest
 * (Habits, Journal, Projects, Topics, Review, Spaces, Calendar, Admin...) are
 * navigate-only shortcuts, which is still the bulk of the "faster" ask.
 */
const MODULES = [
  { key: "today",    label: "Today",          path: "/today",            aliases: ["today"] },
  { key: "dashboard",label: "Dashboard",       path: "/dashboard",        aliases: ["dashboard", "dash"] },
  { key: "planner",  label: "Planner",         path: "/planner",          aliases: ["planner", "plan"],            types: ["Planner"] },
  { key: "tasks",    label: "Tasks",           path: "/tasks",            aliases: ["tasks", "task"] },
  { key: "habits",   label: "Habits",          path: "/habits",           aliases: ["habits", "habit"] },
  { key: "notes",    label: "Notes",           path: "/notes",            aliases: ["notes", "note"],              types: ["Note"] },
  { key: "journal",  label: "Journal",         path: "/journal",          aliases: ["journal"] },
  { key: "finance",  label: "Finance",         path: "/finance",          aliases: ["finance", "expense", "expenses", "money"],
    types: ["Transaction", "Account", "Category", "Merchant", "Tag", "Investment", "Recurring", "StockTrade", "StockWatch", "TradingAccount"] },
  { key: "calendar", label: "Calendar",        path: "/calendar",         aliases: ["calendar", "cal"] },
  { key: "files",    label: "Files",           path: "/files",            aliases: ["files", "file"],              types: ["File"] },
  { key: "projects", label: "Projects",        path: "/projects",         aliases: ["projects", "project", "proj"] },
  { key: "topics",   label: "Topics",          path: "/topics",           aliases: ["topics", "topic"] },
  { key: "review",   label: "Weekly Review",   path: "/review",           aliases: ["review"] },
  { key: "spaces",   label: "Spaces",          path: "/spaces",           aliases: ["spaces", "space"] },
  { key: "search",   label: "Search",          path: "/search",           aliases: ["search"] },
  { key: "upcoming", label: "Upcoming Dates",  path: "/upcoming",         aliases: ["upcoming", "dates"] },
  { key: "admin",    label: "Admin",           path: "/admin",            aliases: ["admin"] },
];

const SOURCE_LABELS = {
  Note: "Notes", Planner: "Planner", File: "Files", Transaction: "Transactions",
  Account: "Accounts", Category: "Categories", Merchant: "Merchants", Tag: "Tags",
  Investment: "Investments", Recurring: "Recurring", StockTrade: "Stock Trades",
  StockWatch: "Stock Watch", TradingAccount: "Trading Accounts",
};

// Where clicking a result actually takes you — items with a per-id edit route
// (Note, Planner) jump straight to that item; everything else opens the
// closest list page it lives on (no per-id route exists for those yet).
function resultRoute(result) {
  switch (result.source) {
    case "Note": return `/notes/edit/${result.id}`;
    case "Planner": return `/planner/edit/${result.id}`;
    case "File": return "/files";
    case "Transaction": return "/finance/transactions";
    case "Account": return "/finance/accounts";
    case "Category": return "/finance/categories";
    case "Merchant": return "/finance/merchants";
    case "Tag": return "/finance/tags";
    case "Investment": return "/finance/investments";
    case "Recurring": return "/finance/recurring";
    case "StockTrade":
    case "StockWatch":
    case "TradingAccount": return "/finance/stocks";
    default: return "/search";
  }
}

function parseInput(raw) {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("/")) return { slash: false, term: trimmed };
  const body = trimmed.slice(1);
  const idx = body.indexOf("/");
  if (idx === -1) return { slash: true, moduleToken: body, term: "" };
  return { slash: true, moduleToken: body.slice(0, idx), term: body.slice(idx + 1) };
}

function matchModules(token) {
  const t = token.trim().toLowerCase();
  if (!t) return MODULES;
  const starts = MODULES.filter((m) => m.aliases.some((a) => a.startsWith(t)));
  if (starts.length) return starts;
  return MODULES.filter((m) => m.aliases.some((a) => a.includes(t)));
}

export default function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setResults([]);
    setActiveIndex(0);
  }, []);

  // Global triggers: Ctrl/Cmd+K, and a custom event (used by the sidebar button)
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      } else if (e.key === "Escape" && open) {
        close();
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("lifeos:open-command-palette", onOpenEvent);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("lifeos:open-command-palette", onOpenEvent);
    };
  }, [open, close]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const parsed = useMemo(() => parseInput(query), [query]);

  const exactModule = useMemo(() => {
    if (!parsed.slash) return null;
    const t = (parsed.moduleToken || "").trim().toLowerCase();
    if (!t) return null;
    return MODULES.find((m) => m.aliases.includes(t)) || null;
  }, [parsed]);

  const candidateModules = useMemo(() => {
    if (!parsed.slash) return [];
    if (exactModule) return [];
    return matchModules(parsed.moduleToken || "");
  }, [parsed, exactModule]);

  // Debounced live search — scoped (module has `types`) or unscoped (freeform query).
  useEffect(() => {
    clearTimeout(debounceRef.current);
    const shouldSearch = parsed.slash
      ? Boolean(exactModule && exactModule.types && parsed.term.trim())
      : Boolean(query.trim());

    if (!shouldSearch) {
      setResults([]);
      setLoading(false);
      return;
    }

    const searchTerm = parsed.slash ? parsed.term.trim() : query.trim();
    const types = parsed.slash ? exactModule.types : undefined;

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const myReqId = ++reqIdRef.current;
      try {
        const res = await apiFetch(`${u}/search`, {
          method: "POST",
          body: JSON.stringify({
            query: searchTerm,
            limit: parsed.slash ? 20 : 10,
            ...(types ? { types } : {}),
          }),
        });
        if (myReqId !== reqIdRef.current) return; // stale response, ignore
        if (!res.ok) { setResults([]); return; }
        const data = await res.json();
        setResults(data?.results || []);
      } catch {
        if (myReqId === reqIdRef.current) setResults([]);
      } finally {
        if (myReqId === reqIdRef.current) setLoading(false);
      }
    }, 250);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, parsed.slash, parsed.term, exactModule]);

  useEffect(() => { setActiveIndex(0); }, [query, results]);

  const goTo = useCallback((path) => { navigate(path); close(); }, [navigate, close]);
  const openResult = useCallback((result) => { navigate(resultRoute(result)); close(); }, [navigate, close]);

  // Flatten whatever's currently rendered into one activatable list, so
  // ArrowUp/Down + Enter work identically regardless of which branch is showing.
  const rows = useMemo(() => {
    if (!parsed.slash) {
      if (!query.trim()) return MODULES.map((m) => ({ type: "module", module: m }));
      return results.map((r) => ({ type: "result", result: r }));
    }
    if (exactModule) {
      const goRow = { type: "module", module: exactModule };
      if (!exactModule.types) return [goRow];
      if (!parsed.term.trim()) return [goRow];
      return [goRow, ...results.map((r) => ({ type: "result", result: r }))];
    }
    return candidateModules.map((m) => ({ type: "module", module: m }));
  }, [parsed, exactModule, candidateModules, results, query]);

  const activate = useCallback((row) => {
    if (!row) return;
    if (row.type === "module") goTo(row.module.path);
    else openResult(row.result);
  }, [goTo, openResult]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, rows.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); activate(rows[activeIndex] || rows[0]); }
  };

  if (!open) return null;

  return (
    <div className="cmdk-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="cmdk-box">
        <div className="cmdk-input-row">
          <span className="cmdk-input-icon">⌕</span>
          <input
            ref={inputRef}
            className="cmdk-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Type /planner to jump, /planner/rent to search Planner, or just search everything…"
          />
          <kbd className="cmdk-esc">Esc</kbd>
        </div>

        <div className="cmdk-body">
          {parsed.slash && !exactModule && parsed.moduleToken && (
            <div className="cmdk-hint">No module matches “/{parsed.moduleToken}” — showing closest matches:</div>
          )}
          {parsed.slash && exactModule && exactModule.types && !parsed.term.trim() && (
            <div className="cmdk-hint">Type a search term after the second “/” to search inside {exactModule.label}.</div>
          )}
          {parsed.slash && exactModule && !exactModule.types && (
            <div className="cmdk-hint">Search isn’t available inside {exactModule.label} yet — press Enter to open it.</div>
          )}
          {loading && <div className="cmdk-hint">Searching…</div>}
          {!loading && (parsed.slash ? exactModule?.types && parsed.term.trim() : query.trim()) && results.length === 0 && (
            <div className="cmdk-hint">No matches.</div>
          )}

          {rows.length === 0 && !loading && (
            <div className="cmdk-empty">Nothing to show yet — start typing.</div>
          )}

          <div className="cmdk-list">
            {rows.map((row, i) => (
              <button
                key={row.type === "module" ? `m-${row.module.key}` : `r-${row.result.source}-${row.result.id}`}
                type="button"
                className={`cmdk-row ${i === activeIndex ? "cmdk-row-active" : ""}`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => activate(row)}
              >
                {row.type === "module" ? (
                  <>
                    <span className="cmdk-row-icon">→</span>
                    <span className="cmdk-row-title">Go to {row.module.label}</span>
                    {row.module.types && <span className="cmdk-row-tag">searchable</span>}
                  </>
                ) : (
                  <>
                    <span className="cmdk-row-source">{SOURCE_LABELS[row.result.source] || row.result.source}</span>
                    <span className="cmdk-row-title">{row.result.title}</span>
                    {row.result.snippet && <span className="cmdk-row-snippet">{row.result.snippet}</span>}
                  </>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="cmdk-footer">
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>Enter</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
          <span className="cmdk-footer-spacer" />
          <span>⌘K / Ctrl+K anywhere to reopen</span>
        </div>
      </div>
    </div>
  );
}
